import { CSSObject, darken, lighten, PaletteMode, ThemeOptions } from '@mui/material'
import { blue, green, grey, orange, red } from '@mui/material/colors'
import colors from './colors'

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    link: true;
    gradient: true;
  }
  interface ButtonPropsColorOverrides {
    grey: true;
  }
}

const spacing = (...spacing: number[]) => {
  if (!Array.isArray(spacing)) {
    spacing = [ spacing ]
  }
  return spacing.map(s => `${s * 8}px`).join(' ')
}

export const gradientStyles: CSSObject = {
  background: 'linear-gradient(180deg, #E8E8E8 25.97%, #DCD7D7 100%)',
  boxShadow: '0 2px 4px rgb(0, 0, 0, .25)',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}

export const getDesignTokens = (mode: PaletteMode): ThemeOptions => {
  return {
    spacing,
    breakpoints: {
      // values: { xs: 0, sm: 600, md: 900, lg: 1240, xl: 1440 },
    },
    shape: {},
    typography: {
      fontFamily: [
        'Inter',
        'Helvetica',
        'Arial',
        'sans-serif'
      ].join(','),
    },
    components: {
      MuiButton: {
        defaultProps: {
          'aria-hidden': false
        },
        styleOverrides: {
          root: {
            variants: [
              {
                props: {
                  variant: 'link',
                },
                style: {
                  padding: 0,
                  margin: 0,
                  minHeight: 0,
                  minWidth: 0,
                  lineHeight: 'normal',
                  textTransform: 'none',
                  textDecoration: 'underline',
                  '&:hover': {
                    textDecoration: 'none',
                  },
                  '&.MuiButton-colorPrimary': {
                    color: colors.primary,
                  },
                  '&.MuiButton-colorSecondary': {
                    color: colors.secondary,
                  },
                  '&.MuiButton-colorInfo': {
                    color: blue[600],
                  },
                  '&.MuiButton-colorError': {
                    color: red[600],
                  },
                  '&.MuiButton-colorWarning': {
                    color: orange[600],
                  },
                  '&.MuiButton-colorSuccess': {
                    color: green[600],
                  },
                  '&.MuiButton-linkInherit': {
                    color: 'inherit',
                  },
                  '.MuiTouchRipple-root': {
                    display: 'none',
                  }
                },
              },
              {
                props: {
                  variant: 'gradient',
                },
                style: {
                  ...gradientStyles
                }
              },
              {
                props: {
                  color: 'grey'
                },
                style: {
                  color: grey[700],
                }
              }
            ],
            minWidth: 40,
            '&.MuiButton-sizeLarge': {
              fontSize: 17,
            }
          },
          fullWidth: {
            '&:has(.MuiButton-endIcon)': {
              justifyContent: 'space-between',
            }
          }
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: '1px solid rgba(0, 0, 0, .35)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '1px solid rgba(0, 0, 0, .5)',
            },
            '.MuiOutlinedInput-input::-webkit-search-cancel-button': {
              border: '1px solid transparent',
              cursor: 'pointer',
              opacity: 1,
              marginRight: -2
            },
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: 14,
            marginBottom: spacing(0.75),
            '&.Mui-disabled': {
              backgroundColor: '#E8E8E8',
              padding: spacing(0.2, 1),
              top: spacing(-0.2),
              left: spacing(-1),
              borderRadius: spacing(0.75, 0.75, 0, 0),
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            '&:not(.MuiDialog-paperFullScreen)': {
              maxHeight: 'calc(100% - 25px)',
              margin: spacing(3)
            }
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            scrollbarWidth: 'thin'
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          spacing: {
            padding: spacing( 2, 3 ),
            '& > :not(style)~:not(style)': {
              marginLeft: spacing(1.5),
            }
          }
        }
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            margin: spacing(0.5, 0.25, 0),
          }
        }
      },
      MuiMenuItem: {
        defaultProps: {
          'aria-hidden': false
        }
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          variant: 'outlined',
          fullWidth: true,
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            'input:-internal-autofill-selected, input:-webkit-autofill, input:-webkit-autofill:focus': {
              boxShadow: '0 0 0px 40rem #FFF inset',
              WebkitTextFillColor: 'unset'
            },
            '&.Mui-disabled': {
              input: {
                color: 'rgb(126, 126, 126)',
                WebkitTextFillColor: 'rgb(126, 126, 126)',
                '&:-internal-autofill-selected, &:-webkit-autofill, &:-webkit-autofill:focus': {
                  boxShadow: '0 0 0px 40rem #DDD inset',
                  WebkitTextFillColor: '#666'
                }
              }
            }
          },
        }
      },
      MuiFormControl: {
        defaultProps: {
          size: 'small',
        }
      },
      MuiPaper: {
        defaultProps: {
          elevation: 1
        }
      }
    },
    palette: {
      mode,
      ...(
        mode === 'light' ? {
          primary: {
            main: colors.primary,
            light: lighten(colors.primary, 0.17), // #335b93
            dark: darken(colors.primary, 0.6),
          },
          secondary: {
            main: colors.secondary,
            light: lighten(colors.secondary, 0.2),
            dark: darken(colors.secondary, 0.1),
          },
          success: {
            main: green[600],
            light: green[400],
            dark: green[800],
          }
        } : {
          primary: {
            main: lighten(colors.primary, 0.3),
            light: lighten(colors.primary, 0.17), // #335b93
            dark: darken(colors.primary, 0.6),
          },
          secondary: {
            main: darken(colors.secondary, 0.2),
            light: lighten(colors.secondary, 0.2),
            dark: darken(colors.secondary, 0.1),
          }
        }
      )
    }
  }
}
